import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, MapPin } from "lucide-react";
import { PHONE_TEL, ADDRESS, scrollToSection } from "@/lib/hours";

const LINES = ["Authentiques.", "Italiennes.", "Généreuses."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" data-testid="hero-section" ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1622880833523-7cf1c0bd4296?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxhcnRpc2FuJTIwcGl6emElMjB3b29kJTIwZmlyZWQlMjBvdmVufGVufDB8fHx8MTc4ODMzNTg1OHww&ixlib=rb-4.1.0&q=85"
          alt="Four à bois"
          className="hidden h-[120%] w-full object-cover sm:block"
        />
        <img
          src="https://images.unsplash.com/photo-1606152196365-d1ce5ea838b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHszfHxhcnRpc2FuJTIwcGl6emElMjB3b29kJTIwZmlyZWQlMjBvdmVufGVufDB8fHx8MTc4ODMzNTg1OHww&ixlib=rb-4.1.0&q=85"
          alt="Four à bois"
          className="h-[120%] w-full object-cover brightness-[0.62] sm:hidden"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/35 sm:via-background/85 sm:to-background/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-7xl px-5 pb-20 pt-40 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 flex flex-wrap items-center gap-3"
        >
          <span data-testid="hero-takeaway-badge" className="badge-tag badge-fav">
            Uniquement à emporter
          </span>
          <span className="badge-tag border-border text-dim inline-flex items-center gap-1.5">
            <MapPin size={11} /> {ADDRESS}
          </span>
        </motion.div>

        <h1
          data-testid="hero-kinetic-title"
          className="font-display text-[6.8vw] font-extrabold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-[#FDF6EC]" : i === 2 ? "text-terra" : ""}`}
                style={i === 1 ? { textShadow: "0 2px 26px rgba(30, 20, 10, 0.35)" } : undefined}
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.6 }}
          className="mt-6 max-w-xl text-base text-dim sm:text-lg"
        >
          Pizzas artisanales à emporter à Arnas, près de Villefranche-sur-Saône.
          Appelez, on prépare, vous récupérez.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a data-testid="hero-phone-order-button" href={PHONE_TEL} className="btn-primary flame-glow">
            <Phone size={17} /> Appeler pour commander
          </a>
          <button data-testid="hero-view-carte-button" onClick={() => scrollToSection("carte")} className="btn-ghost">
            Découvrir la carte <ArrowDown size={16} />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
